import type { PropsWithChildren } from 'react'

import clsx from 'clsx'

import type { Message, RenderContent } from '../../model/types'

import { api } from '../../api'
import { filledContentValue, normalizeAttachmentUrl } from '../../utils/utils'
import { MessageActions } from '../MessageActions'
import { FilePreview } from './FilePreview'
import css from './Message.module.css'
import { Seen } from './Seen.tsx'
import { UnSupportedContent } from './UnSupportedContent.tsx'
import { processText } from './utils'

const BLOB_TYPES: (keyof RenderContent)[] = ['audio', 'file', 'vector_image', 'external_media']

export function MessageBody(message: Message) {
	const reply = message.render_content.find(
		content => content.replied_message_content !== null
	)?.replied_message_content

	return (
		<>
			{reply && (
				<blockquote
					className={css.reply}
				>{`${reply.sender}: ${reply.text}`}</blockquote>
			)}

			<Text {...message} />
			{message.render_content.map((content, index) => (
				<Content content={content} key={index} message={message} />
			))}
		</>
	)
}

const ReactionWrap = ({ children, ...message }: PropsWithChildren<Message>) => (
	<div className={css.hover}>
		{children}
		<MessageActions className={css.actions} message={message} />
	</div>
)

function Content({
	content: current,
	message
}: { content: RenderContent; message: Message }) {
	const renderKey = filledContentValue(current)!
	const content = current?.[renderKey]

	if (content && 'sender' in content) {
		return null
	}

	const attachQuery = api.useAttachmentQuery(
		{
			accepts: content?.recommended_accept_header ?? '',
			id: message.message_id,

			url: content?.url ?? ''
		},
		{
			skip: !content || !BLOB_TYPES.includes(renderKey)
		}
	)

	let url = content?.url ?? ''

	if (attachQuery.isSuccess) {
		url = normalizeAttachmentUrl(attachQuery.data, content?.url ?? '')
	}
		if (attachQuery.isLoading) {
			return (
				<ReactionWrap {...message}>
					<div className={css.placeholder} />
				</ReactionWrap>
			)
		}

		if (!attachQuery.isSuccess) {
			return null
		}


	switch (renderKey) {
		case 'audio':
			return (
				<ReactionWrap {...message}>
					<audio controls src={url} />
				</ReactionWrap>
			)
		case 'conversation_ads_message_content':
		case 'message_ad_render_content':
			return (
				<ReactionWrap {...message}>
					<UnSupportedContent eventName="Ad message" />
				</ReactionWrap>
			)
		case 'external_media':
			return (
				<ReactionWrap {...message}>
					<img alt="gif" src={url} />
				</ReactionWrap>
			)
		case 'file':
			return (
				<ReactionWrap {...message}>
					<FilePreview file={content} url={url} />
				</ReactionWrap>
			)
		case 'forwarded_message_content':
			return (
				<ReactionWrap {...message}>
					<UnSupportedContent eventName="Forwarded message" />
				</ReactionWrap>
			)
		case 'host_urn_data':
		case 'unavailable_content':
			return (
				<ReactionWrap {...message}>
					<UnSupportedContent eventName="This message" />
				</ReactionWrap>
			)
		case 'vector_image':
			return (
				<ReactionWrap {...message}>
					<img
						alt="chat"
						className={css.image}
						height={190}
						src={url}
						width={200}
					/>
				</ReactionWrap>
			)
		case 'video':
			return (
				<ReactionWrap {...message}>
					<UnSupportedContent eventName="Video message" />
				</ReactionWrap>
			)
		case 'video_meeting':
			return (
				<ReactionWrap {...message}>
					<UnSupportedContent eventName="Video meeting" />
				</ReactionWrap>
			)
	}
}

function Text(message: Message) {
	if (!message.text) {
		return null
	}

	const isEditedMessage = message.render_format === 'EDITED'
	const isDeleted = message.render_format === 'RECALLED'

	return (
		<div className={clsx(css.text, css.hover)}>
			<blockquote className={css.textWrapper}>
				<span className={css.textInner}>{processText(message.text)}</span>
				{!isDeleted && <Seen {...message} />}
			</blockquote>

			{isEditedMessage && <span style={{ color: '#565657' }}>(Edited)</span>}
			<MessageActions className={css.actions} isText message={message} />
		</div>
	)
}
